import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from '@mui/material';
import { DirectionsBus, AccessTime } from '@mui/icons-material';
import { orderBy } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import { Route } from '../types/route';

export const RoutesPage: React.FC = () => {
  const { adminType } = useAuth();
  const navigate = useNavigate();

  const { data: routes, loading } = useFirestoreQuery<Route>(
    'routes',
    orderBy('name', 'asc')
  );

  // 권한별 노선 필터링
  const getFilteredRoutes = () => {
    if (adminType === 'super') {
      return routes;
    } else if (adminType === 'route1') {
      // 강서구청 포함 노선
      return routes.filter((route) => route.name.includes('강서구청'));
    } else if (adminType === 'route2') {
      // 하단 포함 노선
      return routes.filter((route) => route.name.includes('하단'));
    }
    return [];
  };

  const filteredRoutes = getFilteredRoutes();

  const handleRouteClick = (routeId: string) => {
    navigate(`/routes/${routeId}`);
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <DirectionsBus sx={{ fontSize: 32, color: '#667eea' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              노선 정보
            </Typography>
            <Typography variant="body2" color="text.secondary">
              셔틀버스 노선 정보를 확인하고 관리합니다.
            </Typography>
          </Box>
        </Box>

        {/* 노선 목록 테이블 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, width: '60%' }}>노선명</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>출발 시간</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>정류장 수</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRoutes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        등록된 노선이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRoutes.map((route) => (
                    <TableRow
                      key={route.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRouteClick(route.id)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsBus sx={{ color: '#667eea', fontSize: 20 }} />
                          <Typography variant="body2" fontWeight={600}>
                            {route.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {route.departureTime}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${route.stops?.length || 0}개`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </MainLayout>
  );
};
